/* eslint-disable @typescript-eslint/naming-convention */

import { OFFER_BUNDLE, VALID_RENDERING_BUTTON_TYPES } from "@cart-client/constants/index";
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import {
    ExtraItem,
    ExtraItemTypes,
    IItem,
    IUpdateItem,
    PlanDetails,
    PresentationInfo,
    RemovePlanMessage,
    SelectedOption,
    SwiggyOneInfo,
} from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/core-ui";

export const shouldShowHalfCard = (item: IUpdateItem) =>
    Boolean(item.nudge_type === ExtraItemTypes.NUDGE_TYPE_AUTO_ADD && item.remove_plan_message);

type IHalfCardApiCallCondition = Record<ExtraItemTypes, boolean>;

export const getHalfCardApiCallCondition = (selectedOption?: SelectedOption | null) => ({
    [ExtraItemTypes.NUDGE_TYPE_AUTO_ADD]: selectedOption === SelectedOption.REMOVE,
    [ExtraItemTypes.NUDGE_TYPE_ONE_TAP]: selectedOption === SelectedOption.ADD,
});

const extractHalfConditionValue = (item: IUpdateItem, condition: IHalfCardApiCallCondition | null): boolean | null => {
    if (item.nudge_type && condition) {
        return condition[item.nudge_type as ExtraItemTypes];
    }
    return null;
};

const isSelectedOptionValid = (selectedOption?: SelectedOption | null): boolean =>
    selectedOption !== undefined && selectedOption !== null;

export const getHalfCardCondition = (item: IUpdateItem, selectedOption?: SelectedOption | null): boolean => {
    const condition = isSelectedOptionValid(selectedOption) ? getHalfCardApiCallCondition(selectedOption) : null;

    const halfCardCondition = extractHalfConditionValue(item, condition);

    if (halfCardCondition !== null) return halfCardCondition;

    return true;
};

type IPlan = { plan_id: string; quantity: number };

export const getPlan = (item: IItem): IPlan[] => {
    if (!item || !item.plan_details) return [];

    return [
        {
            plan_id: item.plan_details.plan_id.toString(),
            quantity: 1,
        },
    ];
};

export const isValidType = (type?: string) =>
    Boolean(
        type &&
            [
                VALID_RENDERING_BUTTON_TYPES.OFFER_BUNDLE_BENEFITS,
                VALID_RENDERING_BUTTON_TYPES.OFFER_BUNDLE_NUDGE,
            ].includes(type.toUpperCase()),
    );

const isOfferBundleProgram = (cartResponse: ReviewedCart | null) =>
    cartResponse?.data?.swiggyOneInfo?.presentationInfo?.plan_details?.program?.toUpperCase() === OFFER_BUNDLE.PROGRAM;

const getOfferPackNudge = (cartResponse: ReviewedCart | null): SwiggyOneInfo | null | undefined => {
    if (isOfferBundleProgram(cartResponse)) {
        return cartResponse?.data?.swiggyOneInfo;
    }
    return null;
};
export const shouldShowHalfCardOnMount = (
    cartResponse: ReviewedCart | null,
): boolean | PresentationInfo | undefined => {
    const offerPackNudge = getOfferPackNudge(cartResponse);

    if (!isEmpty(offerPackNudge?.presentationInfo?.m2c_card_comms)) {
        return offerPackNudge?.presentationInfo;
    }

    return false;
};

export const isOfferPackEnabled = (cartResponse: ReviewedCart | null) =>
    cartResponse?.data?.user_super_status_details?.super_plan_program === OFFER_BUNDLE.PROGRAM;

export const isOfferBundleTooltip = (key: string) => key === OFFER_BUNDLE.TOOLTIP;

export const getExtraItems = (cartResponse: ReviewedCart | null): ExtraItem[] => {
    return cartResponse?.data?.extra_items || [];
};

export const getShowHalfCardItem = (item: IItem | boolean): IItem | boolean => {
    if (!isEmpty(item)) return item;

    return false;
};

type IHalfCardItemAndPlan = {
    halfCardItem?: RemovePlanMessage;
    planDetails?: PlanDetails;
    isM2CFlow?: boolean;
};

export const getHalfCardItemAndPlanDetails = (item: IItem | boolean): IHalfCardItemAndPlan => {
    const halfCardObject = getShowHalfCardItem(item);

    let halfCardItem;
    let planDetails;
    let isM2CFlow;

    if (halfCardObject && typeof halfCardObject !== "boolean") {
        if (!isEmpty(halfCardObject.remove_plan_message)) {
            halfCardItem = halfCardObject.remove_plan_message;
        } else if (!isEmpty(halfCardObject.m2c_card_comms)) {
            halfCardItem = halfCardObject.m2c_card_comms;
            isM2CFlow = true;
        }

        if (!isEmpty(halfCardObject.plan_details)) {
            planDetails = halfCardObject.plan_details;
        }
    }

    return { halfCardItem, planDetails, isM2CFlow };
};

export const filterContext = (context: string, isOneProgramType: string) => {
    if (isEmpty(isOneProgramType)) {
        return context;
    }

    return JSON.stringify({ ...JSON.parse(context), one_type: isOneProgramType });
};
